import React, { useEffect, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { api, fmtDate } from "../../lib/api";
import { AlertTriangle, Info } from "lucide-react";
import { Th, Td, SectionTitle, Loading, usd, num } from "./shared";

const LEVER_COLOR = { rightsizing: "#00579e", binpack: "#6d28d9", spot: "#15803d", instance_swap: "#b45309" };

export const KubeLog = ({ clusterId }) => {
  const [data, setData] = useState(null);
  const [severity, setSeverity] = useState("all");

  useEffect(() => {
    setData(null);
    api.kube.log(clusterId, severity, 80).then(setData).catch(console.error);
  }, [clusterId, severity]);

  if (!data) return <Loading label="Loading activity log…" testId="kube-log-loading" />;

  return (
    <div className="space-y-6" data-testid="kube-log">
      {!!data.blocked_findings.length && (
        <div className="cs-card p-5" data-testid="kube-blocked-findings">
          <SectionTitle hint="Minimum-capacity blocks are missed savings — surfaced as findings, not buried in log lines.">
            Blocked consolidation · findings
          </SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><Th>Node group</Th><Th>Finding</Th><Th right>Est. savings / mo</Th><Th>First seen</Th></tr></thead>
              <tbody>
                {data.blocked_findings.map((b, i) => (
                  <tr key={i} className="border-t border-border" data-testid={`kube-blocked-${i}`}>
                    <Td mono>{b.node_group}</Td>
                    <Td>{b.finding}</Td>
                    <Td right mono className="font-semibold text-[#be185d]">{usd(b.estimated_monthly_savings_usd)}</Td>
                    <Td mono className="text-muted-foreground">{fmtDate(b.first_seen)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="cs-card p-5" data-testid="kube-log-table">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <SectionTitle hint="Every automated action carries its reason and, where it changed a workload, a ChangeSet ID.">
            Activity log · {num(data.entries.length)} entries
          </SectionTitle>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="kube-log-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              {data.severities.map((s) => (
                <SelectItem key={s} value={s} data-testid={`kube-log-sev-${s}`}>{s === "all" ? "All severities" : s.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[560px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-white"><tr><Th>Time</Th><Th>Severity</Th><Th>Action</Th><Th>Message</Th><Th>Reason</Th><Th>ChangeSet</Th></tr></thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} className="border-t border-border align-top" data-testid={`kube-log-${e.id}`}>
                  <Td mono className="text-muted-foreground whitespace-nowrap">{fmtDate(e.ts)}</Td>
                  <Td>
                    <span className={`inline-flex items-center gap-1 font-semibold ${e.severity === "WARN" ? "text-[#92400e]" : "text-muted-foreground"}`}>
                      {e.severity === "WARN" ? <AlertTriangle className="w-3 h-3" /> : <Info className="w-3 h-3" />}{e.severity}
                    </span>
                  </Td>
                  <Td mono style={{ color: LEVER_COLOR[e.lever] || "inherit" }}>{e.kind.replace(/_/g, " ")}</Td>
                  <Td className="max-w-[420px]">{e.message}{e.zones && <div className="text-[10px] text-muted-foreground mt-0.5">zones: {e.zones}</div>}</Td>
                  <Td className="text-muted-foreground max-w-[260px]">{e.reason}</Td>
                  <Td mono className="text-[#00579e]">{e.changeset_id || "—"}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KubeLog;
